import React, { useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import type { CellState, StepStatus } from '../../../api/migrationRunner';

/** Shared bits of the Database page (approved mockup "Database redesign", 2026-10-11). */

export type Tone = 'ok' | 'bad' | 'warn' | 'info' | 'vio' | 'mut';

const TONE: Record<Tone, string> = {
  ok: 'bg-green-100 text-green-700',
  bad: 'bg-red-100 text-red-700',
  warn: 'bg-amber-100 text-amber-800',
  info: 'bg-blue-100 text-blue-700',
  vio: 'bg-violet-100 text-violet-700',
  mut: 'bg-slate-100 text-slate-500',
};

export const Pill: React.FC<{ tone: Tone; children: React.ReactNode; title?: string }> = ({ tone, children, title }) => (
  <span title={title} className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11.5px] font-bold ${TONE[tone]}`}>
    {children}
  </span>
);

export const Panel: React.FC<{ title?: React.ReactNode; actions?: React.ReactNode; children: React.ReactNode; className?: string; bodyClass?: string }> = ({
  title, actions, children, className = '', bodyClass = 'p-4',
}) => (
  <section className={`overflow-hidden rounded-xl border border-slate-200 bg-white ${className}`}>
    {(title || actions) && (
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 px-4 py-3">
        {title && <h3 className="mr-auto text-sm font-bold text-slate-900">{title}</h3>}
        {actions}
      </div>
    )}
    <div className={bodyClass}>{children}</div>
  </section>
);

type BtnKind = 'primary' | 'ghost' | 'danger' | 'success';
const BTN: Record<BtnKind, string> = {
  primary: 'bg-blue-600 text-white hover:bg-blue-700 border-transparent',
  ghost: 'bg-white text-slate-700 hover:bg-slate-50 border-slate-300',
  danger: 'bg-red-600 text-white hover:bg-red-700 border-transparent',
  success: 'bg-green-600 text-white hover:bg-green-700 border-transparent',
};

export const Btn: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement> & { kind?: BtnKind; small?: boolean }> = ({
  kind = 'primary', small = false, className = '', type = 'button', ...rest
}) => (
  <button
    type={type}
    {...rest}
    className={`inline-flex items-center justify-center gap-1.5 rounded-lg border font-bold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:cursor-not-allowed disabled:opacity-50 ${small ? 'px-2.5 py-1 text-xs' : 'px-3.5 py-2 text-[13px]'} ${BTN[kind]} ${className}`}
  />
);

export const Alert: React.FC<{ tone: 'bad' | 'warn' | 'info' | 'ok'; title: React.ReactNode; children?: React.ReactNode; action?: React.ReactNode }> = ({
  tone, title, children, action,
}) => {
  const cls = {
    bad: 'border-red-500 bg-red-50',
    warn: 'border-amber-400 bg-amber-50',
    info: 'border-blue-300 bg-blue-50',
    ok: 'border-green-300 bg-green-50',
  }[tone];
  return (
    <div className={`flex flex-wrap items-center gap-3 rounded-xl border-[1.5px] px-4 py-3 ${cls}`}>
      <div className="min-w-0 flex-1 text-[13.5px] text-slate-700">
        <b className="block text-slate-900">{title}</b>
        {children}
      </div>
      {action}
    </div>
  );
};

export const Kpi: React.FC<{ label: string; value: React.ReactNode; tone?: 'bad' | 'warn' | 'plain'; foot?: string }> = ({ label, value, tone = 'plain', foot }) => (
  <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
    <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{label}</div>
    <div className={`mt-1 text-2xl font-bold tabular-nums ${tone === 'bad' ? 'text-red-600' : tone === 'warn' ? 'text-amber-700' : 'text-slate-900'}`}>{value}</div>
    {foot && <div className="mt-0.5 text-[11.5px] text-slate-500">{foot}</div>}
  </div>
);

/** Right-side sheet; Escape closes. */
export const Drawer: React.FC<{ open: boolean; onClose: () => void; title: React.ReactNode; children: React.ReactNode; wide?: boolean }> = ({
  open, onClose, title, children, wide = false,
}) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-slate-900/30" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        className={`flex h-full w-full flex-col bg-slate-50 shadow-2xl ${wide ? 'max-w-5xl' : 'max-w-3xl'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b border-slate-200 bg-white px-5 py-4">
          <div className="min-w-0 flex-1 text-base font-bold text-slate-900">{title}</div>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 space-y-4 overflow-y-auto p-5">{children}</div>
      </div>
    </div>
  );
};

/** Centered dialog; Escape closes. */
export const Dialog: React.FC<{ open: boolean; onClose: () => void; title: React.ReactNode; children: React.ReactNode; footer?: React.ReactNode }> = ({
  open, onClose, title, children, footer,
}) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      <div role="dialog" aria-modal="true" className="flex max-h-[90vh] w-full max-w-xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-slate-200 px-5 py-4 text-base font-bold text-slate-900">{title}</div>
        <div className="flex-1 space-y-3 overflow-y-auto px-5 py-4 text-sm text-slate-700">{children}</div>
        {footer && <div className="flex flex-wrap justify-end gap-2 border-t border-slate-200 px-5 py-3">{footer}</div>}
      </div>
    </div>
  );
};

/** A radio "choice card" (the mockup's .choice). */
export const Choice: React.FC<{ checked: boolean; onChange: () => void; title: string; children?: React.ReactNode; name: string; recommended?: boolean }> = ({
  checked, onChange, title, children, name, recommended,
}) => (
  <label className={`block cursor-pointer rounded-xl border-[1.5px] px-3.5 py-3 text-[13px] ${checked ? 'border-blue-600 bg-blue-50/60' : 'border-slate-200 bg-white hover:border-slate-300'}`}>
    <input type="radio" name={name} checked={checked} onChange={onChange} className="mr-2 align-middle" />
    <b className="text-slate-900">{title}</b>
    {recommended && <span className="ml-2"><Pill tone="info">recommended</Pill></span>}
    {children && <div className="mt-1 text-slate-500">{children}</div>}
  </label>
);

// ── Status vocab ────────────────────────────────────────────────────────────

export const CELL_LABEL: Record<CellState, string> = {
  applied: 'Applied',
  partial: 'Partly applied',
  failed: 'Failed (nothing applied)',
  interrupted: 'Interrupted',
  resolved: 'Resolved by hand',
  running: 'Running',
  not_run: 'Not run',
  no_db: 'No database',
};

export const CELL_CLASS: Record<CellState, string> = {
  applied: 'bg-green-100 text-green-700',
  partial: 'bg-amber-500 text-white',
  failed: 'bg-red-600 text-white',
  interrupted: 'bg-amber-100 text-amber-800 outline-2 outline-dashed outline-amber-500',
  resolved: 'bg-violet-100 text-violet-700',
  running: 'bg-blue-600 text-white',
  not_run: 'bg-slate-100 text-slate-400',
  no_db: 'bg-transparent text-slate-400 border-[1.5px] border-dashed border-slate-300',
};

const CELL_GLYPH: Record<CellState, React.ReactNode> = {
  applied: (
    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3.5 8.5l3 3 6-7" />
    </svg>
  ),
  partial: '½',
  failed: '!',
  interrupted: '?',
  resolved: 'R',
  running: '…',
  not_run: '',
  no_db: '–',
};

export const StateCell: React.FC<{ state: CellState; title?: string; onClick?: () => void }> = ({ state, title, onClick }) => (
  <button
    type="button"
    title={title ?? CELL_LABEL[state]}
    aria-label={title ?? CELL_LABEL[state]}
    onClick={onClick}
    disabled={!onClick}
    className={`inline-grid h-[26px] w-[26px] place-items-center rounded-md text-xs font-extrabold ${CELL_CLASS[state]} ${onClick ? 'cursor-pointer hover:ring-2 hover:ring-blue-300' : 'cursor-default'}`}
  >
    {CELL_GLYPH[state]}
  </button>
);

export const STEP_TONE: Record<StepStatus, Tone> = {
  pending: 'mut', running: 'info', success: 'ok', failed: 'bad', partial: 'warn', locked: 'bad',
  blocked: 'bad', skipped: 'warn', not_run: 'mut', cancelled: 'mut', interrupted: 'warn',
};

export const STEP_LABEL: Record<StepStatus, string> = {
  pending: 'waiting', running: 'running', success: 'done', failed: 'failed', partial: 'partly applied',
  locked: 'busy (locked)', blocked: 'blocked', skipped: 'skipped', not_run: 'not run', cancelled: 'cancelled', interrupted: 'interrupted',
};

export const RUN_TONE: Record<string, Tone> = {
  success: 'ok', failed: 'bad', partial: 'warn', interrupted: 'warn', resolved: 'vio', running: 'info',
  rolled_back: 'mut', pending: 'mut', skipped: 'mut',
};

export const RUN_LABEL: Record<string, string> = {
  success: 'applied', failed: 'failed', partial: 'partly applied', interrupted: 'interrupted', resolved: 'resolved',
  running: 'running', rolled_back: 'rolled back', pending: 'pending', skipped: 'skipped',
};

export const BATCH_TONE: Record<string, Tone> = { queued: 'mut', running: 'info', paused: 'warn', done: 'ok', failed: 'bad', cancelled: 'mut', interrupted: 'warn' };

// ── Formatting ─────────────────────────────────────────────────────────────

export function when(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export function bytes(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  if (n < 1024) return `${n} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let v = n / 1024;
  let u = 0;
  while (v >= 1024 && u < units.length - 1) { v /= 1024; u += 1; }
  return `${v < 10 ? v.toFixed(1) : Math.round(v)} ${units[u]}`;
}

export function duration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return '—';
  if (ms < 1000) return `${ms} ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)} s`;
  return `${Math.floor(s / 60)} min ${Math.round(s % 60)} s`;
}

/** "migrations-2026-10-11-fuel-location/001-x.sql" → "2026-10-11 fuel-location / 001-x.sql" */
export function shortName(folderPath: string | null | undefined): string {
  if (!folderPath) return '—';
  const [folder, file] = folderPath.split('/');
  const m = /^migrat(?:e|ions)-(\d{4}-\d{1,2}-\d{1,2})(?:-(.+))?$/.exec(folder || '');
  const head = m ? `${m[1]}${m[2] ? ` ${m[2]}` : ''}` : folder;
  return file ? `${head} / ${file}` : head;
}

export const Spinner: React.FC<{ className?: string }> = ({ className = 'h-4 w-4' }) => (
  <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25" />
    <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

export const ErrorBox: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-lg border border-red-200 bg-red-50 p-3 font-mono text-[12px] text-red-800">{children}</pre>
);

/** Segmented control (the mockup's .seg). */
export function Seg<T extends string>({ value, options, onChange }: { value: T; options: { value: T; label: string }[]; onChange: (v: T) => void }) {
  return (
    <div className="inline-flex flex-wrap overflow-hidden rounded-lg border border-slate-300 bg-white">
      {options.map((o, i) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          aria-pressed={value === o.value}
          className={`px-3 py-1.5 text-[12.5px] font-semibold ${i ? 'border-l border-slate-300' : ''} ${value === o.value ? 'bg-blue-50 text-blue-700' : 'text-slate-500 hover:bg-slate-50'}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export interface ToastMsg {
  id: number;
  text: string;
  /** Shows an Undo button; the toast closes once it ran. */
  undo?: () => Promise<void> | void;
}

/** Bottom toast with an optional Undo (closes after 8 s). */
export const Toast: React.FC<{ toast: ToastMsg | null; onClose: () => void }> = ({ toast, onClose }) => {
  const [busy, setBusy] = React.useState(false);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(onClose, 8000);
    return () => clearTimeout(t);
  }, [toast, onClose]);
  if (!toast) return null;
  return (
    <div role="status" className="fixed bottom-5 left-1/2 z-[60] flex max-w-[calc(100%-32px)] -translate-x-1/2 items-center gap-3 rounded-xl bg-slate-900 px-4 py-2.5 text-[13px] font-semibold text-white shadow-2xl">
      <span className="min-w-0">{toast.text}</span>
      {toast.undo && (
        <button
          type="button"
          disabled={busy}
          className="font-extrabold text-blue-300 hover:text-blue-200 disabled:opacity-50"
          onClick={async () => {
            setBusy(true);
            try { await toast.undo?.(); } finally { setBusy(false); onClose(); }
          }}
        >
          {busy ? 'Undoing…' : 'Undo'}
        </button>
      )}
      <button type="button" aria-label="Dismiss" onClick={onClose} className="text-slate-400 hover:text-white">
        <XMarkIcon className="h-4 w-4" />
      </button>
    </div>
  );
};
